import React, { useState, useEffect } from 'react';
import {
    Box, Grid, TextField, Button,
    CircularProgress, Typography, Divider
} from '@mui/material';
import ConfirmationDialog from '../ConfirmationDialog';
import FloatingSnackbar from '../../../common/FloatingSnackbar';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import StudentSelection from './StudentSelection';
import CourseSelection from './CourseSelection';
import DayTimeSelection from './DayTimeSelection';
import DiscountFields from './DiscountFields';
import { parseErrorMessage, formatCourseInfo, formatTimeForError, extractValue } from './utils/errorParsing';

const formatToman = (amount) =>
    `${toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount))} تومان`;

export default function StudentRegistrationForm({
    formKey,
    userRole,
    mode,
    editRecord,
    renewRecord,
    onAfterSubmit,
    onStudentChange
}) {
    const [formData, setFormData] = useState({
        student_id: '',
        teacher_course_id: '',
        day: '',
        start_time: '',
        discount_amount: '',
        discount_type: 'Both_discount'
    });

    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [slotRefreshKey, setSlotRefreshKey] = useState(0);  // Key to force SlotTags refetch
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [selectedTeacherId, setSelectedTeacherId] = useState(null);
    const [selectedCourseData, setSelectedCourseData] = useState(null);
    const editRecordProcessedRef = React.useRef(false);
    
    // Create function for new registrations
    const createStudentRegistration = async (payload) => {
        const axios = (await import('../../../../config/axiosConfig')).default;
        const url = '/operations/api/v1/student-registrations/';
        
        try {
            const response = await axios.post(url, payload);
            
            if (response.status === 200 || response.status === 201) {
                return { success: true, data: response.data };
            } else {
                return { success: false, error: 'Unexpected response status' };
            }
        } catch (error) {
            // Try to extract detailed error information
            const errorData = error.response?.data;
            
            // Check if we have structured error data that should be parsed
            // (error_code, conflicts, or field errors)
            const hasStructuredError = errorData && (
                errorData.error_code || 
                errorData.conflicts || 
                errorData.day || 
                errorData.start_time || 
                errorData.non_field_errors
            );
            
            // If we have structured error data, let parseErrorMessage handle it
            // Otherwise, use the detail or a default message
            const errorMessage = hasStructuredError 
                ? null // Will be parsed by parseErrorMessage
                : (errorData?.detail || error.message || 'خطا در ثبت‌نام');
            
            return { 
                success: false, 
                error: errorMessage,
                errorResponse: error.response
            };
        }
    };

    // Update function for edit mode
    const updateStudentRegistration = async (payload) => {
        const axios = (await import('../../../../config/axiosConfig')).default;
        const url = `/operations/api/v1/student-registrations/${editRecord.id}/`;
        
        try {
            const response = await axios.patch(url, payload);
            
            if (response.status === 200) {
                return { success: true, data: response.data };
            } else {
                return { success: false, error: 'Unexpected response status' };
            }
        } catch (error) {
            // Try to extract detailed error information
            const errorData = error.response?.data;
            
            // Check if we have structured error data that should be parsed
            // (error_code, conflicts, or field errors)
            const hasStructuredError = errorData && (
                errorData.error_code || 
                errorData.conflicts || 
                errorData.day || 
                errorData.start_time || 
                errorData.non_field_errors
            );
            
            // If we have structured error data, let parseErrorMessage handle it
            // Otherwise, use the detail or a default message
            const errorMessage = hasStructuredError 
                ? null // Will be parsed by parseErrorMessage
                : (errorData?.detail || error.message || 'خطا در به‌روزرسانی');
            
            return { 
                success: false, 
                error: errorMessage,
                errorResponse: error.response
            };
        }
    };

    // Initialize form data when editRecord changes
    useEffect(() => {
        // Reset the processed flag when editRecord changes
        editRecordProcessedRef.current = false;
        
        if (editRecord) {
            const initialData = {
                student_id: editRecord.student?.id || '',
                teacher_course_id: editRecord.course?.id || '',
                // Handle day correctly - day 0 is valid (Saturday), so check for null/undefined explicitly
                // Convert to number to ensure consistent type (handles string "0" vs number 0)
                day: editRecord.day !== null && editRecord.day !== undefined ? Number(editRecord.day) : '',
                start_time: editRecord.start_time || '',
                discount_amount: editRecord.discount_amount ? String(editRecord.discount_amount) : '',
                discount_type: editRecord.discount_type || 'Both_discount'
            };
            setFormData(initialData);
            // Set teacher ID and course data from editRecord
            if (editRecord.course?.teacher?.id) {
                setSelectedTeacherId(editRecord.course.teacher.id);
            }
            if (editRecord.course) {
                setSelectedCourseData(editRecord.course);
            }
        } else if (renewRecord) {
            // Renewal handoff (create mode): prefill from the archived enrollment so
            // the secretary only confirms — the new enrollment freezes the CURRENT
            // course fee on the backend. Set the course ref first so the
            // clear-day-on-course-change effect doesn't wipe the prefilled slot.
            prevCourseRef.current = renewRecord.course?.id || '';
            setFormData({
                student_id: renewRecord.student?.id || '',
                teacher_course_id: renewRecord.course?.id || '',
                day: renewRecord.day !== null && renewRecord.day !== undefined ? Number(renewRecord.day) : '',
                start_time: renewRecord.start_time || '',
                discount_amount: '',
                discount_type: 'Both_discount'
            });
            setSelectedTeacherId(renewRecord.course?.teacher?.id || null);
            // current_fee comes from the attendance-page serializer; the fee summary
            // reads selectedCourseData.fee, which must be the LIVE course fee here.
            setSelectedCourseData(
                renewRecord.course
                    ? { ...renewRecord.course, fee: renewRecord.course.current_fee ?? null }
                    : null
            );
            setSlotRefreshKey(prev => prev + 1);
        } else {
            // Create mode: preserve student_id if it exists, clear other fields
            setFormData(prevData => ({
                student_id: prevData.student_id || '', // Keep existing student selection
                teacher_course_id: '',
                day: '',
                start_time: '',
                discount_amount: '',
                discount_type: 'Both_discount'
            }));
            setSelectedTeacherId(null);
            setSelectedCourseData(null);
            // Reset refresh key for create mode
            setSlotRefreshKey(0);
        }
        // For edit mode, the day-specific useEffect will handle the refresh key increment
    }, [editRecord, renewRecord, formKey]);

    // Track previous course to detect course changes
    const prevCourseRef = React.useRef(formData.teacher_course_id);
    
    // Ensure day is cleared when course changes (prevents default day selection).
    // Skipped for the renewal handoff, whose prefilled day/time must survive the
    // seeded course id; manual course changes still clear day/time in
    // handleInputChange.
    useEffect(() => {
        // If course changed and we're in create mode, ensure day is cleared
        const courseChanged = prevCourseRef.current !== formData.teacher_course_id;
        if (courseChanged && mode !== 'edit' && !renewRecord) {
            // Always clear day when course changes (even if it's already empty)
            setFormData(prevData => ({
                ...prevData,
                day: '',
                start_time: ''
            }));
        }
        // Update ref
        prevCourseRef.current = formData.teacher_course_id;
    }, [formData.teacher_course_id, mode, renewRecord]);

    // Force SlotTags refetch when all required data is available in edit mode
    useEffect(() => {
        // When in edit mode, ensure SlotTags refetches when all required fields are set
        if (mode === 'edit' && editRecord && !editRecordProcessedRef.current) {
            const hasStudent = formData.student_id && formData.student_id !== '';
            const hasCourse = formData.teacher_course_id && formData.teacher_course_id !== '';
            // Check if day is valid (including 0 which is Saturday)
            const hasDay = formData.day !== '' && formData.day !== null && formData.day !== undefined;
            
            if (hasStudent && hasCourse && hasDay) {
                // Mark as processed to prevent multiple refetches
                editRecordProcessedRef.current = true;
                // Small delay to ensure formData is fully updated before refetching
                const timer = setTimeout(() => {
                    setSlotRefreshKey(prev => prev + 1);
                }, 200);
                return () => clearTimeout(timer);
            }
        }
    }, [formData.day, formData.student_id, formData.teacher_course_id, mode, editRecord]);

    const handleInputChange = (field, value) => {
        const newFormData = { ...formData, [field]: value };
        
        // Clear time selection when changing student or course
        if (field === 'student_id' || field === 'teacher_course_id') {
            newFormData.start_time = '';
            newFormData.day = '';
        }
        
        setFormData(newFormData);
        
        // Call onStudentChange when student selection changes
        if (field === 'student_id' && onStudentChange) {
            onStudentChange(value);
        }
    };

    const handleDaySelect = (dayValue) => {
        // Clear the selected time slot when changing day
        const newFormData = {
            ...formData,
            day: dayValue,
            start_time: '' // Clear the selected time slot
        };
        setFormData(newFormData);
        
        // Call onStudentChange if needed
        if (onStudentChange) {
            onStudentChange(formData.student_id);
        }
    };

    const performSubmit = async () => {
        if (!formData.student_id || !formData.teacher_course_id) {
            setSubmitError('لطفاً هنرجو و دوره را انتخاب کنید');
            return;
        }

        setSubmitError(null);

        let submitPayload;
        
        if (mode === 'edit' && editRecord?.id) {
            // For PATCH: only send mutable fields
            submitPayload = {
                discount_type: formData.discount_type,
                day: formData.day !== '' ? parseInt(formData.day) : null,
                start_time: formData.start_time || null,
            };
            
            // Only include discount_amount if it has a value
            const discountStr = String(formData.discount_amount ?? '').trim();
            if (discountStr !== '') {
                submitPayload.discount_amount = parseFloat(discountStr);
            }
        } else {
            // For POST: include all required fields
            submitPayload = {
                student_id: parseInt(formData.student_id),
                teacher_course_id: parseInt(formData.teacher_course_id),
                discount_type: formData.discount_type,
                day: formData.day !== '' ? parseInt(formData.day) : null,
                start_time: formData.start_time || null,
            };
            
            // Only include discount_amount if it has a value
            const discountStr = String(formData.discount_amount ?? '').trim();
            if (discountStr !== '') {
                submitPayload.discount_amount = parseFloat(discountStr);
            }
        }

        try {
            setSubmitLoading(true);
            let result;
            let errorResponse = null;
            
            if (mode === 'edit' && editRecord?.id) {
                // Update existing registration
                result = await updateStudentRegistration(submitPayload);
                errorResponse = result.errorResponse;
            } else {
                // Create new registration
                result = await createStudentRegistration(submitPayload);
                errorResponse = result.errorResponse;
            }
            
            if (result.success) {
                onAfterSubmit(mode === 'edit' ? 'update' : 'create', result.data);
                
                // Force SlotTags to refetch after successful update
                if (mode === 'edit') {
                    setSlotRefreshKey(prev => prev + 1);
                }
                
                if (mode === 'create') {
                    // Keep student selected, clear other fields for new registrations                                                                          
                    setFormData(prevData => ({
                        student_id: prevData.student_id, // Keep the selected student                                                                           
                        teacher_course_id: '',
                        day: '',
                        start_time: '',
                        discount_amount: '',
                        discount_type: 'Both_discount'
                    }));
                }
                // Clear any previous errors
                setSubmitError(null);
            } else {
                // Check if we need to fetch course details for conflict errors
                const errorData = (errorResponse || result.errorResponse)?.data;
                const hasConflict = errorData && (
                    errorData.error_code === 'student_time_conflict' || 
                    errorData.error_code === 'teacher_time_conflict'
                ) && errorData.conflicts && errorData.conflicts.length > 0;
                
                if (hasConflict) {
                    // Fetch course details for better error message
                    const conflict = errorData.conflicts[0];
                    const courseId = extractValue(conflict.conflicting_course_id);
                    
                    if (courseId) {
                        // Fetch course details
                        const axios = (await import('../../../../config/axiosConfig')).default;
                        try {
                            const courseResponse = await axios.get(`/operations/api/v1/teachers-and-courses/${courseId}/`);
                            const course = courseResponse.data;
                            const formattedCourse = formatCourseInfo(course);
                            
                            // Build error message with formatted course
                            const startTime = formatTimeForError(conflict.start_time);
                            const endTime = formatTimeForError(conflict.end_time);
                            let timeRange = '';
                            if (startTime && endTime) {
                                timeRange = ` (${toPersianDigits(startTime)} - ${toPersianDigits(endTime)})`;
                            } else if (startTime) {
                                timeRange = ` (${toPersianDigits(startTime)})`;
                            }
                            
                            let errorMessage;
                            if (errorData.error_code === 'student_time_conflict') {
                                errorMessage = `تداخل زمانی با برنامه هنرجو: این هنرجو در این بازه${timeRange} کلاس دیگری دارد. ${formattedCourse}`;
                            } else {
                                errorMessage = `تداخل زمانی با برنامه آموزگار: آموزگار در این بازه${timeRange} کلاس دیگری دارد. ${formattedCourse}`;
                            }
                            
                            setSnackbar({
                                open: true,
                                message: errorMessage,
                                severity: 'error'
                            });
                        } catch (fetchError) {
                            // If fetching course fails, fall back to regular error parsing
                            const errorMessage = parseErrorMessage(
                                result.error === null ? null : result.error, 
                                errorResponse || result.errorResponse,
                                mode
                            );
                            setSnackbar({
                                open: true,
                                message: errorMessage || 'خطایی رخ داد. لطفاً دوباره تلاش کنید.',
                                severity: 'error'
                            });
                        }
                    } else {
                        // No course ID, use regular parsing
                        const errorMessage = parseErrorMessage(
                            result.error === null ? null : result.error, 
                            errorResponse || result.errorResponse
                        );
                        setSnackbar({
                            open: true,
                            message: errorMessage || 'خطایی رخ داد. لطفاً دوباره تلاش کنید.',
                            severity: 'error'
                        });
                    }
                } else {
                    // No conflict or no course details needed, use regular parsing
                    const errorMessage = parseErrorMessage(
                        result.error === null ? null : result.error, 
                        errorResponse || result.errorResponse
                    );
                    setSnackbar({
                        open: true,
                        message: errorMessage || 'خطایی رخ داد. لطفاً دوباره تلاش کنید.',
                        severity: 'error'
                    });
                }
                setSubmitError(null); // Clear inline error, use snackbar instead
            }
        } catch (error) {
            // Check if we need to fetch course details for conflict errors
            const errorData = error.response?.data;
            const hasConflict = errorData && (
                errorData.error_code === 'student_time_conflict' || 
                errorData.error_code === 'teacher_time_conflict'
            ) && errorData.conflicts && errorData.conflicts.length > 0;
            
            if (hasConflict) {
                // Fetch course details for better error message
                const conflict = errorData.conflicts[0];
                const courseId = extractValue(conflict.conflicting_course_id);
                
                if (courseId) {
                    // Fetch course details
                    const axios = (await import('../../../../config/axiosConfig')).default;
                    try {
                        const courseResponse = await axios.get(`/operations/api/v1/teachers-and-courses/${courseId}/`);
                        const course = courseResponse.data;
                        const formattedCourse = formatCourseInfo(course);
                        
                        // Build error message with formatted course
                        const startTime = formatTimeForError(conflict.start_time);
                        const endTime = formatTimeForError(conflict.end_time);
                        let timeRange = '';
                        if (startTime && endTime) {
                            timeRange = ` (${toPersianDigits(startTime)} - ${toPersianDigits(endTime)})`;
                        } else if (startTime) {
                            timeRange = ` (${toPersianDigits(startTime)})`;
                        }
                        
                        let errorMessage;
                        if (errorData.error_code === 'student_time_conflict') {
                            errorMessage = `تداخل زمانی با برنامه هنرجو: این هنرجو در این بازه${timeRange} کلاس دیگری دارد. ${formattedCourse}`;
                        } else {
                            errorMessage = `تداخل زمانی با برنامه آموزگار: آموزگار در این بازه${timeRange} کلاس دیگری دارد. ${formattedCourse}`;
                        }
                        
                        setSnackbar({
                            open: true,
                            message: errorMessage,
                            severity: 'error'
                        });
                    } catch (fetchError) {
                        // If fetching course fails, fall back to regular error parsing
                        const errorMessage = parseErrorMessage(error.message || error, error.response, mode);
                        setSnackbar({
                            open: true,
                            message: errorMessage,
                            severity: 'error'
                        });
                    }
                } else {
                    // No course ID, use regular parsing
                    const errorMessage = parseErrorMessage(error.message || error, error.response);
                    setSnackbar({
                        open: true,
                        message: errorMessage,
                        severity: 'error'
                    });
                }
            } else {
                // No conflict, use regular parsing
                const errorMessage = parseErrorMessage(error.message || error, error.response);
                setSnackbar({
                    open: true,
                    message: errorMessage,
                    severity: 'error'
                });
            }
            setSubmitError(null); // Clear inline error, use snackbar instead
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleSubmit = () => {
        // Show confirmation dialog before submitting
        setShowConfirmDialog(true);
    };

    const handleConfirmSubmit = () => {
        setShowConfirmDialog(false);
        performSubmit();
    };

    const handleCancelSubmit = () => {
        setShowConfirmDialog(false);
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };


    const canSubmit = formData.student_id && formData.teacher_course_id && formData.day !== '' && formData.day !== null && formData.day !== undefined && formData.start_time && formData.start_time !== '';

    // Base fee: live course fee in create mode; in edit mode reconstruct the frozen
    // enrollment fee (final_fee + discount_amount — exact, since discount ≤ fee).
    const baseFee = mode === 'edit' && editRecord
        ? (Number(editRecord.final_fee) || 0) + (Number(editRecord.discount_amount) || 0)
        : (selectedCourseData?.fee ?? null);
    const discountValue = formData.discount_amount && formData.discount_amount !== ''
        ? parseInt(formData.discount_amount, 10) || 0
        : 0;
    const discountExceedsFee = baseFee !== null && discountValue > baseFee;
    const finalFee = baseFee !== null ? Math.max(baseFee - discountValue, 0) : null;

    return (
        <>
            <Box sx={{ p: 2 }}>
                <Grid container spacing={3}>
                    {/* Student Selection */}
                    <StudentSelection
                        value={formData.student_id}
                        onChange={(value) => handleInputChange('student_id', value)}
                        editRecord={editRecord || renewRecord}
                        disabled={mode === 'edit'}
                        formKey={formKey}
                    />

                    {/* Course Selection */}
                    <CourseSelection
                        value={formData.teacher_course_id}
                        onChange={(value) => handleInputChange('teacher_course_id', value)}
                        editRecord={editRecord || renewRecord}
                        disabled={mode === 'edit'}
                        formKey={formKey}
                        onTeacherSelect={(teacherId, courseData) => {
                            setSelectedTeacherId(teacherId);
                            setSelectedCourseData(courseData);
                        }}
                    />

                    {/* Day and Time Selection */}
                    <DayTimeSelection
                        studentId={formData.student_id}
                        teacherCourseId={formData.teacher_course_id}
                        selectedDay={formData.day}
                        selectedTime={formData.start_time}
                        onDaySelect={handleDaySelect}
                        onTimeChange={(timeValue) => handleInputChange('start_time', timeValue)}
                        selectedTeacherId={selectedTeacherId}
                        editRecord={editRecord}
                        mode={mode}
                        slotRefreshKey={slotRefreshKey}
                        enabled={Boolean(formData.student_id && formData.teacher_course_id)}
                    />

                    {/* Discount Fields */}
                    <DiscountFields
                        discountAmount={formData.discount_amount}
                        discountType={formData.discount_type}
                        onDiscountAmountChange={(value) => handleInputChange('discount_amount', value)}
                        onDiscountTypeChange={(value) => handleInputChange('discount_type', value)}
                    />

                    {/* Fee Summary */}
                    {baseFee !== null && (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 3,
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    شهریه دوره: {formatToman(baseFee)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    تخفیف: {formatToman(discountValue)}
                                </Typography>
                                {discountExceedsFee ? (
                                    <Typography variant="body2" color="error">
                                        تخفیف از شهریه بیشتر است.
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                                        شهریه نهایی: {formatToman(finalFee)}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    )}

                    {/* Action Buttons */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            {mode === 'create' && (
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={!canSubmit || submitLoading}
                                    startIcon={submitLoading ? <CircularProgress size={20} /> : null}
                                >
                                    ثبت‌نام
                                </Button>
                            )}
                            
                            {mode === 'edit' && (
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={!canSubmit || submitLoading}
                                    startIcon={submitLoading ? <CircularProgress size={20} /> : null}
                                >
                                    به‌روزرسانی
                                </Button>
                            )}
                        </Box>
                    </Grid>

                </Grid>
            </Box>

            {/* Error Snackbar */}
            <FloatingSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleSnackbarClose}
                anchor={{ vertical: 'bottom', horizontal: 'left' }}
                autoHideDuration={6000}
            />

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                open={showConfirmDialog}
                onClose={handleCancelSubmit}
                onConfirm={handleConfirmSubmit}
                title={mode === 'edit' ? 'تأیید ویرایش' : 'تأیید ثبت‌نام'}
                message={(mode === 'edit'
                    ? 'آیا از ویرایش این ثبت‌نام اطمینان دارید؟'
                    : 'آیا از ثبت‌نام این هنرجو در دوره انتخاب شده اطمینان دارید؟'
                ) + (finalFee !== null && !discountExceedsFee
                    ? ` شهریه نهایی با احتساب تخفیف: ${formatToman(finalFee)}.`
                    : ''
                )}
                confirmText={mode === 'edit' ? 'به‌روزرسانی' : 'ثبت‌نام'}
                cancelText="برگشت"
                confirmColor="primary"
                isLoading={submitLoading}
            />
        </>
    );
}
